import React, { useCallback, useEffect, useMemo, useState, Component } from 'react';
import { Database, Layers, Component as ComponentIcon, Tag } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { type Question, ALL_QUESTIONS_CORE, QUESTIONS_BY_FAMILY, QUESTION_TYPE_REGISTRY } from '../../../content';
import { VISUAL_REGISTRY } from '../../../visuals';
import { OverviewTab } from '../content/questionBank/OverviewTab';
import { TypesTab } from '../content/questionBank/TypesTab';
import { VisualsTab } from '../content/questionBank/VisualsTab';
import { FamiliesTab } from '../content/questionBank/FamiliesTab';
import { QuestionBrowser } from '../content/questionBank/QuestionBrowser';
import { type AdminNavigationContext, type AdminRoute } from '../adminNavigation';
import { pushSearchParams, readAllowedSearchParam, readSearchParam, replaceSearchParams } from '../adminUrlState';
import { ADMIN_MODULE_META } from '../shared/moduleMetadata';
// Subject metadata — derived from canonical source
const SUBJECT_META = ADMIN_MODULE_META;
type TabId = 'overview' | 'types' | 'visuals' | 'families';
const TABS = [{
  id: 'overview' as const,
  label: 'Vakken',
  icon: <Database className="w-4 h-4" data-id="element-7303" data-mp="fbngj4c9jtu9" />
}, {
  id: 'types' as const,
  label: 'Typen',
  icon: <Layers className="w-4 h-4" data-id="element-7304" data-mp="kaglg8dhicit" />
}, {
  id: 'visuals' as const,
  label: 'Visuals',
  icon: <ComponentIcon className="w-4 h-4" data-id="element-7305" data-mp="17yreipcxv7wj" />
}, {
  id: 'families' as const,
  label: 'Families',
  icon: <Tag className="w-4 h-4" data-id="element-7306" data-mp="1c0ut3muwmsm1" />
}] as const;
const TAB_IDS = TABS.map(tab => tab.id) as readonly TabId[];
const BROWSER_MODE_IDS = ['none', 'subject', 'questionType', 'visual', 'family'] as const;
// Browser mode state
type BrowserMode = {
  type: 'none';
} | {
  type: 'subject';
  code: string;
} | {
  type: 'questionType';
  questionType: string;
} | {
  type: 'visual';
  visualType: string;
} | {
  type: 'family';
  familyId: string;
};
interface QuestionBankPageProps {
  questions?: Question[];
  navigationContext?: AdminNavigationContext | null;
  onNavigate?: (route: AdminRoute, context?: AdminNavigationContext) => void;
}
export function QuestionBankPage({
  questions = [],
  navigationContext,
  onNavigate
}: QuestionBankPageProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [browserMode, setBrowserMode] = useState<BrowserMode>({
    type: 'none'
  });
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const allQuestions = useMemo(() => questions.length > 0 ? questions : ALL_QUESTIONS_CORE, [questions]);
  const isQuestionInBrowserMode = useCallback((questionId: string, mode: BrowserMode) => {
    const question = allQuestions.find(item => item.id === questionId);
    if (!question || mode.type === 'none') return false;
    if (mode.type === 'subject') {
      return question.familyId?.split('-')[0] === mode.code;
    }
    if (mode.type === 'questionType') {
      return question.questionType === mode.questionType;
    }
    if (mode.type === 'visual') {
      return [...(question.stimulusAids || []), ...(question.supportAids || [])].some(aid => aid.type === mode.visualType);
    }
    return question.familyId === mode.familyId;
  }, [allQuestions]);
  const readUrlState = useCallback(() => {
    const activeTab = readAllowedSearchParam('tab', TAB_IDS, 'overview');
    const mode = readAllowedSearchParam('mode', BROWSER_MODE_IDS, 'none');
    const value = readSearchParam('value');
    const requestedQuestionId = readSearchParam('question');
    let browserMode: BrowserMode = {
      type: 'none'
    };
    if (value && mode === 'subject') {
      if (allQuestions.some(question => question.familyId?.split('-')[0] === value)) {
        browserMode = {
          type: 'subject',
          code: value
        };
      }
    } else if (value && mode === 'questionType') {
      if (allQuestions.some(question => question.questionType === value)) {
        browserMode = {
          type: 'questionType',
          questionType: value
        };
      }
    } else if (value && mode === 'visual') {
      if (allQuestions.some(question => [...(question.stimulusAids || []), ...(question.supportAids || [])].some(aid => aid.type === value))) {
        browserMode = {
          type: 'visual',
          visualType: value
        };
      }
    } else if (value && mode === 'family') {
      if (allQuestions.some(question => question.familyId === value)) {
        browserMode = {
          type: 'family',
          familyId: value
        };
      }
    }
    return {
      activeTab,
      browserMode,
      selectedQuestionId: requestedQuestionId && isQuestionInBrowserMode(requestedQuestionId, browserMode) ? requestedQuestionId : null
    };
  }, [allQuestions, isQuestionInBrowserMode]);
  const writeUrlState = useCallback((nextTab: TabId, nextMode: BrowserMode, nextQuestionId: string | null, historyMode: 'push' | 'replace' = 'replace') => {
    const value = nextMode.type === 'subject' ? nextMode.code : nextMode.type === 'questionType' ? nextMode.questionType : nextMode.type === 'visual' ? nextMode.visualType : nextMode.type === 'family' ? nextMode.familyId : undefined;
    const updates = {
      tab: nextTab,
      mode: nextMode.type === 'none' ? undefined : nextMode.type,
      value,
      question: nextQuestionId
    };
    if (historyMode === 'push') {
      pushSearchParams(updates);
    } else {
      replaceSearchParams(updates);
    }
  }, []);
  useEffect(() => {
    const syncFromUrl = () => {
      const nextState = readUrlState();
      setActiveTab(nextState.activeTab);
      setBrowserMode(nextState.browserMode);
      setSelectedQuestionId(nextState.selectedQuestionId);
    };
    syncFromUrl();
    window.addEventListener('popstate', syncFromUrl);
    return () => window.removeEventListener('popstate', syncFromUrl);
  }, [readUrlState]);
  useEffect(() => {
    if (navigationContext?.kind !== 'question-bank-question') return;
    const familyMode: BrowserMode = {
      type: 'family',
      familyId: navigationContext.familyId
    };
    const mode: BrowserMode = allQuestions.some(question => question.familyId === navigationContext.familyId) ? familyMode : {
      type: 'none'
    };
    const questionId = isQuestionInBrowserMode(navigationContext.questionId, mode) ? navigationContext.questionId : null;
    setActiveTab('families');
    setBrowserMode(mode);
    setSelectedQuestionId(questionId);
    writeUrlState('families', mode, questionId);
  }, [allQuestions, isQuestionInBrowserMode, navigationContext, writeUrlState]);
  const openBrowser = (nextMode: BrowserMode) => {
    setBrowserMode(nextMode);
    setSelectedQuestionId(null);
    writeUrlState(activeTab, nextMode, null, 'push');
  };
  const closeBrowser = () => {
    setBrowserMode({
      type: 'none'
    });
    setSelectedQuestionId(null);
    writeUrlState(activeTab, {
      type: 'none'
    }, null, 'push');
  };
  const selectBrowserQuestion = (questionId: string | null) => {
    const nextQuestionId = questionId && isQuestionInBrowserMode(questionId, browserMode) ? questionId : null;
    setSelectedQuestionId(nextQuestionId);
    writeUrlState(activeTab, browserMode, nextQuestionId);
  };
  const handleTabChange = (id: string) => {
    const nextTab = id as TabId;
    setActiveTab(nextTab);
    setBrowserMode({
      type: 'none'
    });
    setSelectedQuestionId(null);
    writeUrlState(nextTab, {
      type: 'none'
    }, null);
  };
  // Compute subjects data
  const subjects = useMemo(() => {
    const bySubject: Record<string, {
      count: number;
      families: Set<string>;
      tiers: number[];
    }> = {};
    allQuestions.forEach(q => {
      const code = q.familyId?.split('-')[0] || 'UNK';
      if (!bySubject[code]) {
        bySubject[code] = {
          count: 0,
          families: new Set(),
          tiers: []
        };
      }
      bySubject[code].count++;
      bySubject[code].families.add(q.familyId);
      if (q.tier) bySubject[code].tiers.push(q.tier);
    });
    return Object.entries(bySubject).map(([code, data]) => {
      const meta = SUBJECT_META[code] || {
        name: code,
        color: 'bg-gray-500'
      };
      const avgTier = data.tiers.length > 0 ? Math.round(data.tiers.reduce((a, b) => a + b, 0) / data.tiers.length) : 0;
      return {
        code,
        name: meta.name,
        color: meta.color,
        questionCount: data.count,
        familyCount: data.families.size,
        avgTier
      };
    }).sort((a, b) => b.questionCount - a.questionCount);
  }, [allQuestions]);
  // Compute question types data
  const types = useMemo(() => {
    const byType: Record<string, {
      count: number;
      families: Set<string>;
    }> = {};
    allQuestions.forEach(q => {
      const t = q.questionType;
      if (!byType[t]) {
        byType[t] = {
          count: 0,
          families: new Set()
        };
      }
      byType[t].count++;
      byType[t].families.add(q.familyId);
    });
    return Object.entries(byType).map(([type, data]) => {
      const isRegistered = type in QUESTION_TYPE_REGISTRY;
      return {
        type,
        questionCount: data.count,
        familyCount: data.families.size,
        isRegistered
      };
    }).sort((a, b) => b.questionCount - a.questionCount);
  }, [allQuestions]);
  // Compute visuals data
  const visuals = useMemo(() => {
    const byVisual: Record<string, {
      count: number;
      families: Set<string>;
    }> = {};
    allQuestions.forEach(q => {
      const aids = [...(q.stimulusAids || []), ...(q.supportAids || [])];
      aids.forEach(aid => {
        if (!aid.type) return;
        if (!byVisual[aid.type]) {
          byVisual[aid.type] = {
            count: 0,
            families: new Set()
          };
        }
        byVisual[aid.type].count++;
        byVisual[aid.type].families.add(q.familyId);
      });
    });
    return Object.entries(byVisual).map(([type, data]) => {
      const isRegistered = type in VISUAL_REGISTRY;
      return {
        type,
        questionCount: data.count,
        familyCount: data.families.size,
        isRegistered
      };
    }).sort((a, b) => b.questionCount - a.questionCount);
  }, [allQuestions]);
  // Compute families data
  const families = useMemo(() => {
    return Array.from(QUESTIONS_BY_FAMILY.entries()).map(([id, qs]) => {
      const subject = id.split('-')[0] || 'UNK';
      const tiers = qs.map(q => q.tier).filter(Boolean) as number[];
      const avgTier = tiers.length > 0 ? Math.round(tiers.reduce((a, b) => a + b, 0) / tiers.length) : 0;
      return {
        id,
        subject,
        questionCount: qs.length,
        avgTier
      };
    }).sort((a, b) => a.id.localeCompare(b.id));
  }, []);
  // Derive browser props from mode
  const browserProps = useMemo(() => {
    if (browserMode.type === 'none') return null;
    switch (browserMode.type) {
      case 'subject':
        {
          const meta = SUBJECT_META[browserMode.code] || {
            name: browserMode.code,
            color: 'bg-gray-500'
          };
          const filtered = allQuestions.filter(q => q.familyId?.split('-')[0] === browserMode.code);
          const familyCount = new Set(filtered.map(q => q.familyId)).size;
          return {
            title: meta.name,
            subtitle: `${familyCount} families · ${filtered.length} vragen`,
            questions: filtered,
            accentColor: 'blue' as const
          };
        }
      case 'questionType':
        {
          const filtered = allQuestions.filter(q => q.questionType === browserMode.questionType);
          return {
            title: browserMode.questionType,
            subtitle: `${filtered.length} vragen met dit type`,
            questions: filtered,
            accentColor: 'purple' as const
          };
        }
      case 'visual':
        {
          const filtered = allQuestions.filter(q => {
            const aids = [...(q.stimulusAids || []), ...(q.supportAids || [])];
            return aids.some(a => a.type === browserMode.visualType);
          });
          return {
            title: browserMode.visualType,
            subtitle: `${filtered.length} vragen met deze visual`,
            questions: filtered,
            accentColor: 'emerald' as const
          };
        }
      case 'family':
        {
          const filtered = allQuestions.filter(q => q.familyId === browserMode.familyId);
          return {
            title: browserMode.familyId,
            subtitle: `${filtered.length} vragen`,
            questions: filtered,
            accentColor: 'amber' as const,
            groupBy: 'none' as const
          };
        }
    }
  }, [browserMode, allQuestions]);
  // If browser is active, render it instead of tabs
  if (browserMode.type !== 'none' && browserProps) {
    return <div className="flex flex-col h-full min-h-0 p-4 sm:p-6 pt-2 sm:pt-4 overflow-hidden" data-id="element-7307" data-mp="b6bv6qejif26">
        <QuestionBrowser title={browserProps.title} subtitle={browserProps.subtitle} questions={browserProps.questions} onBack={closeBrowser} accentColor={browserProps.accentColor} groupBy={'groupBy' in browserProps ? browserProps.groupBy : 'family'} selectedQuestionId={selectedQuestionId} onSelectQuestion={selectBrowserQuestion} onNavigateToGovernance={questionId => onNavigate?.('governance', {
        kind: 'governance-question',
        questionId
      })} data-id="element-7308" data-mp="g4wsagyhwime" />
        
      </div>;
  }
  return <PageLayout title="Vragenbank" subtitle="Verken alle vragen, types en visuele componenten" breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Vragenbank'
  }]} data-id="element-7309" data-mp="2jhmce4pv4bek">
      
      <AdminTabs tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} data-id="element-7310" data-mp="2alwsy17z5r3t" />
      

      <div className="mt-4" data-id="element-7311" data-mp="25nvdrrdo6bxm">
        {activeTab === 'overview' && <OverviewTab subjects={subjects} totalQuestions={allQuestions.length} totalFamilies={families.length} typeCount={types.length} visualCount={visuals.length} onBrowseSubject={code => openBrowser({
        type: 'subject',
        code
      })} data-id="element-7312" data-mp="334akxk6mqiuz" />}
        {activeTab === 'types' && <TypesTab questions={allQuestions} onSelectType={type => openBrowser({
        type: 'questionType',
        questionType: type
      })} data-id="element-7313" data-mp="2mwmj8uyga83i" />}
        {activeTab === 'visuals' && <VisualsTab questions={allQuestions} onSelectVisual={type => openBrowser({
        type: 'visual',
        visualType: type
      })} data-id="element-7314" data-mp="28ypng0rtm5pw" />}
        {activeTab === 'families' && <FamiliesTab questions={allQuestions} onSelectFamily={id => openBrowser({
        type: 'family',
        familyId: id
      })} data-id="element-7315" data-mp="31mtqsfmx66pf" />}
      </div>
    </PageLayout>;
}